import { useMemo } from "react";

interface Permission {
  module: string;
  identifier: string;
};

export const useModulePermissions = (module: PermissionModule) => {
  // const permissions = useAppSelector(selectPermissions);
  const permissions: Permission[] = JSON.parse(localStorage.getItem("permissions") ?? "[]");

  return (identifier: PermissionIdentifier): boolean => {
    if (!module) return false;
    return permissions.some(
      (p) => p.module === module && p.identifier === identifier
    );
  };
};

export const checkPermission = (module: PermissionModule, identifier: PermissionIdentifier): boolean => {
  const permissions: Permission[] = JSON.parse(localStorage.getItem("permissions") ?? "[]");

  return permissions.some(
    (permission) => permission.module === module && permission.identifier === identifier
  );
};

export const checkMultiplePermissions = (module: PermissionModule, identifiers: PermissionIdentifier[]): boolean => {
  const permissions: Permission[] = JSON.parse(localStorage.getItem("permissions") ?? "[]");

  return identifiers.some(identifier =>
    permissions.some(
      permission =>
        permission.module === module && permission.identifier === identifier
    )
  );
};

export const isRouteAllowed = (routeToCheck: string): boolean => {
  const menuData = localStorage.getItem("menuItems");
  if (!menuData) {
    return false;
  }

  const menus = JSON.parse(menuData);
  if (!Array.isArray(menus)) {
    return false;
  }

  const searchMenus = (menusList: any[]): boolean => {
    if (!Array.isArray(menusList)) return false;

    for (const menu of menusList) {
      if (menu?.route === routeToCheck) {
        return true;
      }

      if (Array.isArray(menu?.childMenus) && menu.childMenus.length > 0) {
        if (searchMenus(menu.childMenus)) {
          return true;
        }
      }
    }
    return false;
  };

  return searchMenus(menus);
};

export enum PermissionModule {
  RX = "Rx",
  INVENTORY = "Inventory",
  USER_MANAGEMENT = "UserManagement",
  Add_PATIENT_ON_RECORD_USAGE = "AddPatientOnRecordUsage",
  PRODUCT_MASTER = "ProductMaster",
  VENDER_MASTER = "VendorMaster",
  ORDERING_PRODUCT_MASTER = "OrderingProductMaster",
  SETTINGS = "Settings",
  UNIT_MASTER = "UnitMaster",
  ITEM_TYPE_MASTER = "ItemTypeMaster",
  ECW_ORDER_ADMINISTRATION = "EcwOrderAdministration",
  LOCATION_MASTER = "LocationMaster",
  CUSTOM_FIELD_MASTER = "CustomFieldMaster",
  LABEL_SETUP = "LabelSetup",
}

export enum PermissionIdentifier {
  /* ── Rx ──────────────────────────────────────────────────────────────────── */
  MANAGE_USE_UNITS = "ManageUseUnits",
  DUPLICATE_INVENTORY_ITEM = "DuplicateRx",
  EDIT_INVENTORY_ITEM = "EditRx",
  DELETE_INVENTORY_ITEM = "DeleteRx",
  ADD_INVENTORY_ITEM = "AddRx",
  INACTIVE_INVENTORY_ITEM = "InactivateRx",
  /* ── Inventory ───────────────────────────────────────────────────────────── */
  PRINT_BARCODE = "PrintBarcode",
  RECEIVE_INVENTORY = "ReceiveInventory",
  TRANSFER_INVENTORY = "TransferInventory",
  UPDATE_INVENTORY_COUNT = "UpdateCount",
  REPACK_INVENTORY = "RepackInventory",
  ADJUST_INVENTORY = "AdjustInventory",
  CHECK_NDC_ON_INTERNET = "CheckNDCOnInternet",
  VIEW_INVENTORY_REPORT = "ViewInventoryReport",
  ADD_TO_NDC_PRODUCT_MASTER = "AddToNDCProductMaster",
  /* ── User Management ─────────────────────────────────────────────────────── */
  CREATE_USER = "CreateUser",
  EDIT_USER = "EditUser",
  DELETE_USER = "DeleteUser",
  DUPLICATE_USER = "DuplicateUser",
  INACTIVE_USER = "InactivateUser",
  IMPORT_USER = "ImportUser",
  EXPORT_USER = "ExportUser",
  LOAD_USAGE_USER = "LoadUsage",
  RESET_PASSWORD_USER = "ResetPassword",
  /* ── Add Patient On Record Usage ─────────────────────────────────────────── */
  ADD_PATIENT_ON_RECORD_USAGE = "AddPatientOnRecordUsage",
  /* ── Product Master ──────────────────────────────────────────────────────── */
  IMPORT_PRODUCT_MASTER = "importProductMaster",
  EXPORT_PRODUCT_MASTER = "exportProductMaster",
  VIEW_PRODUCT_MASTER = "viewProductMaster",
  CREATE_PRODUCT_MASTER = "createProductMaster",
  EDIT_PRODUCT_MASTER = "editProductMaster",
  DELETE_PRODUCT_MASTER = "deleteProductMaster",
  DUPLICATE_PRODUCT_MASTER = "duplicateProductMaster",
  /* ── Vendor Master ──────────────────────────────────────────────────────── */
  VIEW_VENDOR_MASTER = "viewVendorMaster",
  CREATE_VENDOR_MASTER = "createVendorMaster",
  EDIT_VENDOR_MASTER = "editVendorMaster",
  DELETE_VENDOR_MASTER = "deleteVendorMaster",
  DUPLICATE_VENDOR_MASTER = "duplicateVendorMaster",
  INACTIVE_VENDOR_MASTER = "inactiveVendorMaster",
  /* ── Ordering Product Master ─────────────────────────────────────────────── */
  VIEW_ORDERING_PRODUCT_MASTER = "viewOrderingProduct",
  CREATE_ORDERING_PRODUCT_MASTER = "createOrderingProduct",
  EDIT_ORDERING_PRODUCT_MASTER = "editOrderingProduct",
  DELETE_ORDERING_PRODUCT_MASTER = "deleteOrderingProduct",
  DUPLICATE_ORDERING_PRODUCT_MASTER = "duplicateOrderingProduct",
  INACTIVE_ORDERING_PRODUCT_MASTER = "inactiveOrderingProduct",
  /* ── Settings ───────────────────────────────────────────────────────────── */
  VIEW_SETTINGS = "viewSettings",
  CREATE_SETTINGS = "createSettings",
  EDIT_SETTINGS = "editSettings",
  DELETE_SETTINGS = "deleteSettings",
  CHANGE_COMPANY_LOGO = "changeCompanyLogo",
  /* ── Unit Master ─────────────────────────────────────────────────────────── */
  VIEW_UNIT_MASTER = "viewUnitMaster",
  CREATE_UNIT_MASTER = "createUnitMaster",
  EDIT_UNIT_MASTER = "editUnitMaster",
  DELETE_UNIT_MASTER = "deleteUnitMaster",
  INACTIVE_UNIT_MASTER = "inactiveUnitMaster",
  IMPORT_UNIT_MASTER = "importUnitMaster",
  EXPORT_UNIT_MASTER = "exportUnitMaster",
  /* ── Item Type Master ───────────────────────────────────────────────────── */
  VIEW_ITEM_TYPE = "viewItemType",
  CREATE_ITEM_TYPE = "createItemType",
  EDIT_ITEM_TYPE = "editItemType",
  DELETE_ITEM_TYPE = "deleteItemType",
  INACTIVE_ITEM_TYPE = "inactiveItemType",
  DUPLICATE_ITEM_TYPE = "duplicateItemType",
  /* ── Ecw Order Administration ─────────────────────────────────────────────── */
  Add_PATIENT_ON_ECW = "addPatientOnEcw",
  RECORD_THE_USAGE_ON_ECW = "recordTheUsageOnEcw",
  /* ── Location Master ─────────────────────────────────────────────────────── */
  VIEW_LOCATION_MASTER = "viewLocationMaster",
  CREATE_LOCATION_MASTER = "createLocationMaster",
  EDIT_LOCATION_MASTER = "editLocationMaster",
  DELETE_LOCATION_MASTER = "deleteLocationMaster",
  INACTIVE_LOCATION_MASTER = "inactiveLocationMaster",
  IMPORT_LOCATION_MASTER = "importLocationMaster",
  EXPORT_LOCATION_MASTER = "exportLocationMaster",
  /* ── Custom Field Master ─────────────────────────────────────────────────────── */
  VIEW_CUSTOM_FIELD_MASTER = "viewCustomFields",
  CREATE_CUSTOM_FIELD_MASTER = "createCustomFields",
  EDIT_CUSTOM_FIELD_MASTER = "editCustomFields",
  DELETE_CUSTOM_FIELD_MASTER = "deleteCustomFields",
  INACTIVE_CUSTOM_FIELD_MASTER = "inactiveCustomFields",
  DUPLICATE_CUSTOM_FIELD_MASTER = "duplicateCustomFields",
  /* ── Label Setup ─────────────────────────────────────────────────────── */
  VIEW_LABEL_SETUP = "viewLabelSetup",
  CREATE_LABEL_SETUP = "createLabelSetup",
  EDIT_LABEL_SETUP = "editLabelSetup",
  DELETE_LABEL_SETUP = "deleteLabelSetup",
  DUPLICATE_LABEL_SETUP = "duplicateLabelSetup",
}
